<!-- Spinner while terminology is being loaded -->
<div *ngIf="loading && !loadError" class="pd-loader">
    <hc-progress-spinner color="purple" [diameter]="75"></hc-progress-spinner>
</div>

<!-- Error loading content -->
<div *ngIf="!loading && loadError" class="pd-permission-error">
    <hc-icon fontSet="fa" fontIcon="fa-warning"></hc-icon>
    There was an error loading the term list. Click below try again.
    <button hc-button size="lg" (click)="loadTerms()">Reload Terms</button>
</div>

<div class="admin-terminology-table-container" [hidden]="loadError || loading">
    <div class="admin-terminology-control-bar">
        <button hc-button (click)="openAddEditModal(addEditTermModal, false)">Add Term</button>
        <div class="admin-terminology-control-right">
            <hc-form-field class="admin-terminology-table-filters-filter" [inline]="true">
                <hc-label>Type:</hc-label>
                <hc-select [formControl]="selectedTypesControl" (change)="applyFilter(searchTerm)">
                    <option *ngFor="let type of filterTypes" [ngValue]="type">{{ type }}</option>
                </hc-select>
            </hc-form-field>
            <hc-form-field class="admin-terminology-table-filters-filter" [inline]="true">
                <hc-label>Category:</hc-label>
                <hc-select [formControl]="selectedCategoriesControl" (change)="applyFilter(searchTerm)">
                    <option *ngFor="let cat of filterCategories" [ngValue]="cat">{{ cat }}</option>
                </hc-select>
            </hc-form-field>
            <hc-search-bar
                #searchbar
                placeholder="Search terminology"
                (triggerSearch)="applyFilter($event)"
                class="admin-terminology-search"
            ></hc-search-bar>
        </div>
    </div>
    <table hc-table [borders]="false" [dataSource]="dataSource" hcSort>
        <!-- Name Column -->
        <ng-container hcColumnDef="name">
            <th hc-header-cell *hcHeaderCellDef hc-sort-header id="nameHeader">Term</th>
            <td hc-cell *hcCellDef="let element">{{ element.name }}</td>
        </ng-container>

        <!-- Usage Column -->
        <ng-container hcColumnDef="usage">
            <th hc-header-cell *hcHeaderCellDef hc-sort-header id="usageHeader">Definition or Guideline</th>
            <td hc-cell *hcCellDef="let element" [innerHTML]="element.usage"></td>
        </ng-container>

        <!-- Example Column -->
        <ng-container hcColumnDef="example">
            <th hc-header-cell *hcHeaderCellDef hc-sort-header id="exampleHeader">Usage Example</th>
            <td hc-cell *hcCellDef="let element" [innerHTML]="element.example"></td>
        </ng-container>

        <!-- Type Column -->
        <ng-container hcColumnDef="types">
            <th hc-header-cell *hcHeaderCellDef hc-sort-header id="typesHeader">Types</th>
            <td hc-cell *hcCellDef="let element">{{ element.types }}</td>
        </ng-container>

        <!-- Category Column -->
        <ng-container hcColumnDef="categories">
            <th hc-header-cell *hcHeaderCellDef hc-sort-header id="categoriesHeader">Categories</th>
            <td hc-cell *hcCellDef="let element">{{ element.categories }}</td>
        </ng-container>

        <!-- Date Added Column -->
        <ng-container hcColumnDef="dateAdded">
            <th hc-header-cell *hcHeaderCellDef hc-sort-header id="dateAddedHeader">Date Added</th>
            <td hc-cell *hcCellDef="let element">{{ element.dateAdded | date: 'shortDate'}}</td>
        </ng-container>

        <!-- Actions Column -->
        <ng-container hcColumnDef="actions" justify="center">
            <th hc-header-cell *hcHeaderCellDef id="actionsHeader"></th>
            <td hc-cell *hcCellDef="let element">
                <div class="hc-table-actions">
                    <span
                        hcMenuIcon
                        class="fa fa-trash hc-action-ico"
                        hcTooltip="Remove term"
                        (click)="removeTerm(element)"
                    ></span>
                    <span
                        hcMenuIcon
                        class="fa fa-lightbulb-o hc-action-ico"
                        hcTooltip="Request change"
                        (click)="changeRequest(addRequestModal, element)"
                    ></span>
                    <span 
                        hcMenuIcon
                        class="fa fa-pencil hc-action-ico"
                        hcTooltip="Edit term"
                        (click)="openAddEditModal(addEditTermModal, true, element)"
                    ></span>
                </div>
            </td>
        </ng-container>

        <tr hc-header-row *hcHeaderRowDef="adminTerminologyDisplayedColumns"></tr>
        <tr hc-row *hcRowDef="let row; columns: adminTerminologyDisplayedColumns"></tr>
    </table>

    <div class="hc-empty-table-block" *ngIf="dataSource.filteredData.length === 0">
        <span class="hc-empty-table-block-ico hc-empty-ico"></span>
        <span class="hc-empty-table-block-msg">No terms meet your search criteria.</span>
    </div>

    <hc-pagination
        [length]="adminTerminologyListLength"
        [pageSize]="adminTerminologyPageSize"
        [hidden]="loadError || loading"
    ></hc-pagination>
</div>

<ng-template #addEditTermModal let-dismiss="dismiss" let-data="data">
    <hc-modal>
        <hc-modal-header>{{ data.edit ? 'Edit' : 'Add' }} Term</hc-modal-header>
        <hc-modal-body>
            <form [formGroup]="adminTermControl" class="admin-terminology-form">
                <hc-form-field>
                    <hc-label>Term:</hc-label>
                    <input hcInput formControlName="name" placeholder="Enter term" class="term-input" [required]="true" (input)="termExists = false"/>
                    <hc-error>{{termExists ? 'Term already exists in the list' : 'A term is required'}}</hc-error>
                </hc-form-field>
                <hc-form-field>
                    <hc-label>Date Added:</hc-label>
                    <input hcInput [hcDatepicker]="picker1" formControlName="dateAdded" placeholder="Enter date..." [required]="true" />
                    <hc-datepicker-toggle hcSuffix [for]="picker1"></hc-datepicker-toggle>
                    <hc-datepicker #picker1></hc-datepicker>
                    <hc-error>A valid date is required</hc-error>
                </hc-form-field>
                <hc-form-field>
                    <hc-label>Types:</hc-label>
                    <hc-multiselect class="admin-terminology-select" required>
                        <ng-select
                            [items]="types"
                            [addTag]="false"
                            [multiple]="true"
                            dropdownPosition="bottom"
                            placeholder="Search for types..."
                            formControlName="types"
                        ></ng-select>
                    </hc-multiselect>
                    <hc-error>A type is required</hc-error>
                </hc-form-field>
                <hc-form-field>
                    <hc-label>Categories:</hc-label>
                    <hc-multiselect class="admin-terminology-select" required>
                        <ng-select
                            [items]="categories"
                            [addTag]="false"
                            [multiple]="true"
                            dropdownPosition="bottom"
                            placeholder="Search for categories..."
                            formControlName="categories"
                        ></ng-select>
                    </hc-multiselect>
                    <hc-error>A cateogry is required</hc-error>
                </hc-form-field>
                <hc-form-field>
                    <hc-label>Definition or Guideline:</hc-label>
                    <textarea
                        hcInput
                        rows="4"
                        cols="50"
                        formControlName="usage"
                        placeholder="Enter definition"
                        [required]="true"
                    ></textarea>
                    <hc-error>Definition is required</hc-error>
                </hc-form-field>
                <hc-form-field>
                    <hc-label>Example:</hc-label>
                    <textarea
                        hcInput
                        rows="4"
                        cols="50"
                        formControlName="example"
                        placeholder="Enter example"
                    ></textarea>
                </hc-form-field>
            </form>
        </hc-modal-body>
        <hc-modal-footer>
            <button hc-button buttonStyle="link" (click)="dismiss()">Cancel</button>
            <button
                hc-button
                buttonStyle="primary"
                [disabled]="data.edit ? !adminTermControl.dirty : !adminTermControl.valid"
                (click)="data.edit ? editTerm(data.term) : addTerm()"
            >
                {{ data.edit ? 'Update' : 'Add' }}
            </button>
        </hc-modal-footer>
    </hc-modal>
</ng-template>

<ng-template #addRequestModal let-dismiss="dismiss" let-data="data">
    <hc-modal>
        <hc-modal-header>Add Change Request</hc-modal-header>
        <hc-modal-body>
            <form [formGroup]="addChangeControl">
                <hc-form-field>
                    <hc-label>Term to Change:</hc-label>
                    <input hcInput formControlName="name" placeholder="What term would you like to change?" [required]="true" />
                    <hc-error>A term is required</hc-error>
                </hc-form-field>
                <hc-form-field>
                    <hc-label>Definition/Usage Rule:</hc-label>
                    <input hcInput formControlName="usage" placeholder="What is the definition/usage rule for this term?" [required]="true" />
                    <hc-error>Usage is required</hc-error>
                </hc-form-field>
                <hc-form-field>
                    <hc-label>Comments (optional):</hc-label>
                    <textarea hcInput rows="4" cols="50" formControlName="comments" placeholder="Why are you suggesting this change?" ></textarea>
                </hc-form-field>
                <hc-form-field>
                    <hc-label>Contributor Name:</hc-label>
                    <input hcInput formControlName="contributorName" placeholder="What is your name?" [required]="true" />
                    <hc-error>Contributor name is required</hc-error>
                </hc-form-field>
                <hc-form-field>
                    <hc-label>Contributor Email:</hc-label>
                    <input hcInput formControlName="contributorEmail" placeholder="At what email can we reach you?" [required]="true" />
                    <hc-error>Contributor is required</hc-error>
                </hc-form-field>
            </form>
        </hc-modal-body>
        <hc-modal-footer>
            <button hc-button buttonStyle="link" (click)="dismiss()">Cancel</button>
            <button
                hc-button
                [disabled]="data.edit ? !addChangeControl.dirty : !addChangeControl.valid"
                (click)="dismiss(); submitChangeRequest();"
            >
                Add
            </button>
        </hc-modal-footer>
    </hc-modal>
</ng-template>